import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Divider } from '@/components/ui/daisy';
import { useQuestionFormsOrchestrator } from '@/data/context/QuestionFormsOrchestrator';
import { useFormRegistration } from '@/data/hook/question/useFormRegistration';
import { useQuestionExamGradeCtx } from '@/data/context/QuestionExamGradeContext';
import { PointsSection } from '../fields/PointsSection';

import { OrderingItemCreateDTO } from '@/models/Answer';
import { FeedbackCreateDTO } from '@/models/Feedback';
import { DNDOrdenationQuestion, FlowColumn } from '@/models/Question';
import {
  DEFAULT_LANGUAGE,
  SupportedLanguage,
  TranslatableDict,
} from '@/models/TranslatableField';
import { adapterOrderingAnswers } from '@/utils/questions/adapterAnswersDTO';
import { adapterFeedbacks } from '@/utils/questions/adapterFeedbacks';
import { normalizeTranslatableField } from '@/utils/translatable-field/normalizeTranslatableField';
import { QuestionContextField } from '../fields/QuestionContextField';
import { QuestionTitleField } from '../fields/QuestionTitleField';
import { QuestionFormContainer } from '../QuestionFormContainer';
import QuestionPreview from '@/components/question/forms/QuestionPreview';
import { useOrderingPreviewQuestion } from '@/data/hook/question/useOrderingPreviewQuestion';
import OrderingItemsForm from './OrderingItemsForm';

export type OrderingFormFields = {
  title: TranslatableDict;
  context: TranslatableDict;
  flow: FlowColumn;
  orderingItems: OrderingItemCreateDTO[];
  feedbacks: FeedbackCreateDTO[];
  maxGrade?: number;
};

type OrderingFormProps = {
  isActive?: boolean;
  question: DNDOrdenationQuestion;
  isLoading?: boolean;
  onSubmit: (data: OrderingFormFields) => Promise<DNDOrdenationQuestion>;
};

export default function OrderingForm({
  isActive = true,
  question,
  isLoading,
  onSubmit,
}: OrderingFormProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.questionsTab.createQuestionModal.orderingForm',
  });

  const { identifier } = question;
  const column = question.columns?.at(0);

  const [activeLanguage, setActiveLanguage] =
    useState<SupportedLanguage>(DEFAULT_LANGUAGE);

  const gradeCtx = useQuestionExamGradeCtx();
  const maxGradeDefault = gradeCtx?.getGradeForQuestion(question)?.maxGrade;

  const defaultValues = useMemo<OrderingFormFields>(
    () => ({
      title: normalizeTranslatableField(question?.title),
      context: normalizeTranslatableField(question?.context),
      flow: (column?.flow as FlowColumn) ?? FlowColumn.VERTICAL,
      orderingItems: adapterOrderingAnswers(column?.items ?? []),
      feedbacks: adapterFeedbacks(question?.feedbacks ?? []),
      maxGrade: maxGradeDefault,
    }),
    [question, column?.flow, column?.items, maxGradeDefault],
  );

  const { submitForm } = useQuestionFormsOrchestrator();

  const methods = useForm<OrderingFormFields>({ defaultValues });
  const { watch, trigger, reset, formState } = methods;

  const { hasChanges, handleReset } = useFormRegistration({
    identifier,
    submit: onSubmit,
    validate: trigger,
    watch,
    reset,
    formState,
    defaultValues,
  });

  const realTimeValues = watch();
  const previewQuestion = useOrderingPreviewQuestion(realTimeValues);

  return (
    <div className="flex gap-6">
      <QuestionFormContainer
        methods={methods}
        activeLanguage={activeLanguage}
        onLanguageChange={setActiveLanguage}
        onSubmit={e => {
          e.preventDefault();
          submitForm(identifier);
        }}
        isLoading={isLoading}
        hasChanges={hasChanges}
        onCancel={handleReset}
      >
        <div className="flex flex-col gap-4">
          <QuestionTitleField
            activeLanguage={activeLanguage}
            placeholder={t('titlePlaceholder')}
          />
          <QuestionContextField
            activeLanguage={activeLanguage}
            isActive={isActive}
          />
        </div>

        <Divider />

        <OrderingItemsForm />

        <PointsSection />
      </QuestionFormContainer>

      <div className="flex-1">
        <QuestionPreview question={previewQuestion} />
      </div>
    </div>
  );
}
